import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { type AvatarData } from '@/api';
import { useViewportDevice } from '@/hooks';
import { AnimatedAvatar } from '@/shared/features/user-avatar';
import { Button, DialogHeader, ResponsiveDialog } from '@/ui_v3';
import { AvatarPartSelector } from './components/AvatarPartSelector';
import { AvatarPreviewPanel } from './components/AvatarPreviewPanel';
import { DownloadAvatarButton } from './components/DownloadAvatarButton';
import { RandomAvatarButton } from './components/RandomAvatarButton';
import { type AvatarSaveStrategy } from './hooks/types';
import { type AvatarDecorateForm, AvatarDecorateSchema } from './schema';

/** footer bar height — matches core mobileLayout.ts MOBILE_DECORATE_AVATAR_FOOTER_HEIGHT_PX */
const MOBILE_FOOTER_HEIGHT_PX = 72;
const getMobileFooterInset = () => `calc(${MOBILE_FOOTER_HEIGHT_PX}px + env(safe-area-inset-bottom, 0px))`;
interface AvatarDecorateModalProps {
  /** 현재 아바타 (없으면 기본 파츠). */
  initialAvatar?: AvatarData | null;
  onClose: () => void;
  /** 저장 전략 주입: /profile/edit=프로필 DB, play=게임 SET_AVATAR. */
  onSave: AvatarSaveStrategy['save'];
  isSaving?: boolean;
}
const toFormValue = (avatar?: AvatarData | null): AvatarDecorateForm => ({
  avatar: {
    skinId: avatar?.skinId ?? 1,
    faceId: avatar?.faceId ?? 1,
    hairId: avatar?.hairId ?? 1,
    clothesId: avatar?.clothesId ?? 1,
    accessoryIds: avatar?.accessoryIds as Record<string, number> ?? {}
  }
});
export const AvatarDecorateModal = ({
  initialAvatar,
  onClose,
  onSave,
  isSaving
}: AvatarDecorateModalProps) => {
  const {
    t
  } = useTranslation('common');
  const {
    isMobile
  } = useViewportDevice();
  const form = useForm<AvatarDecorateForm>({
    resolver: zodResolver(AvatarDecorateSchema),
    defaultValues: toFormValue(initialAvatar)
  });
  const handleSave = form.handleSubmit(async ({
    avatar
  }) => {
    try {
      await onSave(avatar);
      onClose();
    } catch (error) {
      logger.captureError(error);
    }
  });
  const avatar = form.watch('avatar');
  const isDirty = form.formState.isDirty;
  if (isMobile) {
    return <ResponsiveDialog size="lg" className="h-[580px]" onClose={onClose}>
        <FormProvider {...form}>
          <DialogHeader title={t('avatarDecorate.title')} hasCloseButton onClose={onClose} className="border-b border-gray-200" />
          {/* Mobile layout: small preview top, parts middle, fixed button bar bottom */}
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            {/* Small preview — scale 1, 88x79, no motion toggle */}
            <div className="flex items-center justify-center">
              <div className="relative flex h-[79px] w-[88px] items-center justify-center pb-[15px]">
                <AnimatedAvatar avatarData={avatar} behavior="stand" />
              </div>
            </div>
            {/* Parts selector — bottom-padded to clear fixed button bar */}
            <div className="flex-1 overflow-hidden" style={{
            paddingBottom: getMobileFooterInset()
          }}>
              <AvatarPartSelector />
            </div>
          </div>
          {/* Fixed bottom button bar — matches core .mobile_button_section */}
          <div className="fixed bottom-0 left-0 right-0 flex items-center justify-between gap-3 border-t border-gray-200 bg-white p-4">
            <div className="flex gap-3">
              <DownloadAvatarButton />
              <RandomAvatarButton />
            </div>
            <Button variant="contained" intent="strong" size="md" className="h-[40px] w-[210px]" disabled={!isDirty || isSaving} onClick={handleSave}>
              {t('avatarDecorate.save')}
            </Button>
          </div>
        </FormProvider>
      </ResponsiveDialog>;
  }
  return <ResponsiveDialog size="lg" className="flex h-[540px] flex-col mobile-lg:w-[780px]" onClose={onClose} data-sentry-element="ResponsiveDialog" data-sentry-component="AvatarDecorateModal" data-sentry-source-file="AvatarDecorateModal.tsx">
      <FormProvider {...form} data-sentry-element="FormProvider" data-sentry-source-file="AvatarDecorateModal.tsx">
        <DialogHeader title={t('avatarDecorate.title')} hasCloseButton onClose={onClose} className="border-b border-gray-200" data-sentry-element="DialogHeader" data-sentry-source-file="AvatarDecorateModal.tsx" />
        <div className="flex min-h-0 flex-1 flex-col mobile-lg:flex-row">
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <AvatarPartSelector data-sentry-element="AvatarPartSelector" data-sentry-source-file="AvatarDecorateModal.tsx" />
          </div>
          <div className="flex shrink-0 flex-col border-gray-200 mobile-lg:w-[270px] mobile-lg:border-l">
            <AvatarPreviewPanel onSave={handleSave} isSaving={isSaving} data-sentry-element="AvatarPreviewPanel" data-sentry-source-file="AvatarDecorateModal.tsx" />
          </div>
        </div>
      </FormProvider>
    </ResponsiveDialog>;
};